import { cn } from '@/utilities/ui'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'
import { ArrowIcon } from '@/components/ui/LinkButton/ArrowIcon'
import { ExternalLinkIcon } from '@/components/ui/LinkButton/ExternalLinkIcon'

export const buttonVariants = cva(
  [
    'font-button font-weight-button leading-button tracking-button',
    'inline-flex items-center justify-center gap-[12px] whitespace-nowrap',
    'transition-colors focus-visible:outline-hidden',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus-ring',
    'enabled:cursor-pointer',
  ],
  {
    defaultVariants: {
      size: 'default',
      variant: 'olive',
    },
    variants: {
      size: {
        clear: 'text-button',
        default: 'text-button h-12 px-6 py-2',
        icon: 'text-button h-12 w-12',
      },
      variant: {
        olive: 'bg-olive-2 text-white hover:bg-highlighter hover:text-olive-2',
        punch: 'bg-punch text-white hover:bg-highlighter hover:text-olive-2',
        zest: 'bg-zest text-white hover:bg-highlighter hover:text-olive-2',
        highlighter: 'bg-highlighter text-white hover:bg-highlighter/90',
        slime: 'bg-slime text-dark hover:bg-highlighter hover:text-olive-2',
        powder: 'bg-powder text-white hover:bg-highlighter hover:text-olive-2',
        outline:
          'border-2 text-white border-border/25 bg-olive-2 hover:bg-highlighter hover:text-olive-2',
        'outline-dark':
          'border-2 border-olive-2 text-dark bg-transparent hover:border-highlighter hover:bg-highlighter hover:text-olive-2',
        'link-olive':
          'text-olive-2 items-center justify-center underline-offset-4 hover:underline !px-0',
        'link-punch':
          'text-punch items-center justify-center underline-offset-4 hover:underline !px-0',
        'link-zest':
          'text-zest items-center justify-center underline-offset-4 hover:underline !px-0',
        'link-highlighter':
          'text-highlighter items-center justify-center underline-offset-4 hover:underline !px-0',
        'link-slime':
          'text-slime items-center justify-center underline-offset-4 hover:underline !px-0',
        'link-powder':
          'text-powder items-center justify-center underline-offset-4 hover:underline !px-0',
        'link-light':
          'text-light items-center justify-center underline-offset-4 hover:underline !px-0',
        ghost: '',
      },
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  ref?: React.Ref<HTMLButtonElement>
  linkIcon?: 'internal' | 'external' | 'none'
}

export const Button: React.FC<ButtonProps> = ({
  className,
  size,
  variant,
  ref,
  linkIcon = 'none',
  children,
  ...props
}) => {
  return (
    <button className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props}>
      {children}
      {linkIcon === 'internal' ? (
        <span>
          <ArrowIcon />
        </span>
      ) : linkIcon === 'external' ? (
        <span>
          <ExternalLinkIcon />
        </span>
      ) : null}
    </button>
  )
}
